import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, Clock, Send, Loader2, CheckCircle2, Building2, LifeBuoy } from 'lucide-react';
import PhoneInput from '../../components/ui/PhoneInput';
import LegalLayout from '../../components/LegalLayout';
import { COMPANY } from './companyConfig';

const ContactUs = () => {
  const [form, setForm] = useState({ name: '', email: '', phone: '', message: '', topic: 'general' });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  // Contact form submits are opt-in and end up as a support ticket if the
  // backend endpoint is wired; we fall back to a mailto: link so it works even
  // before we wire a server-side handler.
  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) return;
    setSubmitting(true);
    const subject = encodeURIComponent(`[${form.topic}] ${COMPANY.brand} enquiry from ${form.name}`);
    const body = encodeURIComponent(
      `Name: ${form.name}\nEmail: ${form.email}\nPhone: ${form.phone}\n\n${form.message}`
    );
    // Open the user's mail client; in a follow-up session we can wire this to
    // `/api/public/contact` that creates a support ticket server-side.
    window.location.href = `mailto:${COMPANY.supportEmail}?subject=${subject}&body=${body}`;
    setTimeout(() => {
      setSubmitting(false);
      setDone(true);
    }, 600);
  };

  return (
    <LegalLayout
      title="Contact Us"
      subtitle={`We're here to help with bookings, refunds, agent onboarding, and everything in between.`}
      canonicalPath="/contact-us"
      description="Contact ACF Tourism for flight bookings, refunds, visa assistance, and travel agent onboarding. Mumbai-based support since 2002."
    >
      <div className="not-prose mb-6 p-4 bg-orange-50 border border-orange-200 rounded-xl flex items-start gap-3">
        <LifeBuoy className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-gray-800">
          <p className="font-semibold text-gray-900">Already have a booking?</p>
          <p className="mt-1">Raise a ticket from <Link to="/my-tickets" className="text-orange-700 font-semibold underline">Help &amp; Tickets</Link> or use &quot;Need Help&quot; on My Bookings for faster resolution with your PNR attached.</p>
        </div>
      </div>
      <div className="not-prose grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        {/* Contact cards */}
        <div className="space-y-4">
          <div className="flex items-start gap-3 p-4 bg-gradient-to-br from-[#F5FBFB] to-white border border-[#659A9B]/20 rounded-xl">
            <div className="w-10 h-10 rounded-xl bg-[#659A9B]/15 flex items-center justify-center flex-shrink-0">
              <Building2 className="w-5 h-5 text-[#1B375B]" />
            </div>
            <div>
              <div className="font-semibold text-[#1B375B]">{COMPANY.legalName}</div>
              <div className="text-sm text-gray-600 mt-1">{COMPANY.address}</div>
            </div>
          </div>

          <div className="flex items-start gap-3 p-4 bg-gradient-to-br from-[#F5FBFB] to-white border border-[#659A9B]/20 rounded-xl">
            <div className="w-10 h-10 rounded-xl bg-[#659A9B]/15 flex items-center justify-center flex-shrink-0">
              <Phone className="w-5 h-5 text-[#1B375B]" />
            </div>
            <div>
              <div className="font-semibold text-[#1B375B]">Call us</div>
              <a href={`tel:${COMPANY.phone.replace(/\s+/g, '')}`}
                 className="text-sm text-gray-700 hover:text-[#1B375B] block mt-1">{COMPANY.phone}</a>
              <div className="text-xs text-gray-500 mt-1 flex items-center gap-1"><Clock className="w-3 h-3" /> {COMPANY.phoneHours}</div>
            </div>
          </div>

          <div className="flex items-start gap-3 p-4 bg-gradient-to-br from-[#F5FBFB] to-white border border-[#659A9B]/20 rounded-xl">
            <div className="w-10 h-10 rounded-xl bg-[#659A9B]/15 flex items-center justify-center flex-shrink-0">
              <Mail className="w-5 h-5 text-[#1B375B]" />
            </div>
            <div>
              <div className="font-semibold text-[#1B375B]">Email</div>
              <a href={`mailto:${COMPANY.supportEmail}`} className="text-sm text-gray-700 hover:text-[#1B375B] block mt-1">
                {COMPANY.supportEmail} <span className="text-xs text-gray-400">(bookings / refunds)</span>
              </a>
              <a href={`mailto:${COMPANY.salesEmail}`} className="text-sm text-gray-700 hover:text-[#1B375B] block">
                {COMPANY.salesEmail} <span className="text-xs text-gray-400">(sales / agent onboarding)</span>
              </a>
              <a href={`mailto:${COMPANY.grievanceOfficer.email}`} className="text-sm text-gray-700 hover:text-[#1B375B] block">
                {COMPANY.grievanceOfficer.email} <span className="text-xs text-gray-400">(grievance officer)</span>
              </a>
            </div>
          </div>

          <div className="flex items-start gap-3 p-4 bg-gradient-to-br from-[#F5FBFB] to-white border border-[#659A9B]/20 rounded-xl">
            <div className="w-10 h-10 rounded-xl bg-[#659A9B]/15 flex items-center justify-center flex-shrink-0">
              <MapPin className="w-5 h-5 text-[#1B375B]" />
            </div>
            <div>
              <div className="font-semibold text-[#1B375B]">Registration</div>
              <div className="text-xs text-gray-600 mt-1">
                CIN: {COMPANY.cin}<br/>
                GSTIN: {COMPANY.gstin}<br/>
                PAN: {COMPANY.pan}
              </div>
            </div>
          </div>
        </div>

        {/* Form */}
        <div className="bg-white border border-gray-200 rounded-xl p-5 md:p-6">
          {done ? (
            <div className="text-center py-8" data-testid="contact-success">
              <CheckCircle2 className="w-12 h-12 text-emerald-600 mx-auto mb-3" />
              <h3 className="font-semibold text-[#1B375B] text-lg">Message ready to send</h3>
              <p className="text-sm text-gray-600 mt-2">
                Your mail client should have opened with a pre-filled email to{' '}
                <a href={`mailto:${COMPANY.supportEmail}`} className="text-[#659A9B] font-medium">{COMPANY.supportEmail}</a>.
                If nothing happened, please email us directly. We typically respond within 24 hours.
              </p>
              <button
                onClick={() => { setDone(false); setForm({ name: '', email: '', phone: '', message: '', topic: 'general' }); }}
                className="mt-5 text-sm text-[#659A9B] hover:text-[#1B375B] font-semibold"
              >
                Send another message
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="contact-form">
              <h3 className="font-semibold text-[#1B375B] text-lg mb-2">Send us a message</h3>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1.5">Full name</label>
                <input
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Your name"
                  data-testid="contact-name"
                  className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#659A9B]/30 focus:border-[#659A9B]"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5">Email</label>
                  <input
                    type="email"
                    required
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    placeholder="you@example.com"
                    data-testid="contact-email"
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#659A9B]/30 focus:border-[#659A9B]"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5">Phone</label>
                  <PhoneInput
                    value={form.phone}
                    onChange={(v) => setForm({ ...form, phone: v })}
                    defaultCountry="IN"
                    testid="contact-phone"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1.5">Topic</label>
                <select
                  value={form.topic}
                  onChange={(e) => setForm({ ...form, topic: e.target.value })}
                  data-testid="contact-topic"
                  className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#659A9B]/30 focus:border-[#659A9B]"
                >
                  <option value="general">General enquiry</option>
                  <option value="booking">Existing booking</option>
                  <option value="refund">Refund / cancellation</option>
                  <option value="agent">Agent onboarding (B2B)</option>
                  <option value="partnership">Partnership / supplier</option>
                  <option value="grievance">Grievance</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1.5">Message</label>
                <textarea
                  required
                  rows={5}
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  placeholder="How can we help?"
                  data-testid="contact-message"
                  className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-sm resize-none focus:outline-none focus:ring-2 focus:ring-[#659A9B]/30 focus:border-[#659A9B]"
                />
              </div>

              <button
                type="submit"
                disabled={submitting}
                data-testid="contact-submit"
                className="w-full py-3.5 bg-gradient-to-r from-[#1B375B] to-[#335E77] text-white rounded-xl font-bold shadow hover:shadow-lg transition-all flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" /> Preparing message…
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4" /> Send message
                  </>
                )}
              </button>

              <p className="text-[11px] text-gray-500">
                For urgent booking issues, please call {COMPANY.phone}.
              </p>
            </form>
          )}
        </div>
      </div>

      <h2>Operating Hours</h2>
      <p>
        Our phone support is open {COMPANY.phoneHours}. For emergencies outside these hours
        (flight cancellations within 12 hours of departure), email{' '}
        <a href={`mailto:${COMPANY.supportEmail}`}>{COMPANY.supportEmail}</a> — on-call staff
        check this inbox every 2 hours.
      </p>

      <h2>Business Address</h2>
      <p>
        {COMPANY.address}<br/>
        Please email or call before visiting; we operate by appointment only.
      </p>
    </LegalLayout>
  );
};

export default ContactUs;
